import * as React from 'react';
import { translate } from 'components/translate-hoc';
import * as moment from 'moment';

// utils
import { formatHours, localizeMonth, localizeDate } from 'utils/localizeTime';
import URLParams from 'utils/url-params';

import PlatformButton from 'components/platform-selection-button';

// constants
import { PLATFORM_URL_PARAMETER, TEXT_KEYS } from './platform-selection.constants';
import { PLATFORM_TYPES } from '@sitegen/common/constants/platforms';

// types
import { ITranslationProps } from 'types/translation';
import { IEventPlatform } from 'types/platforms';

import './platform-selection.styles.less';
import AnalyticsService from 'modules/analytics-provider/analytics-provider.service';
import { PLATFORM_BUTTON_FLOW_ACTION } from './platform-selection.constants';
import { CATEGORY } from 'modules/analytics-provider/analytics-provider.constants';
import { LANDING_FLOW_STEP } from '../../screens/platform-selection/platform-selection-screen.constants';

export class PlatformSelection extends React.Component<IPlatformSelectionProps & ITranslationProps> {
    public componentDidMount() {
        this.handleURLParameters();
    }

    public handleURLParameters(): void {
        const { eventPlatforms, setPlatform } = this.props;

        const isPCSelectedInURL = URLParams().get(PLATFORM_URL_PARAMETER) === PLATFORM_TYPES.PC;
        const eventPlatformPC = eventPlatforms.find(({ platform }) => platform === PLATFORM_TYPES.PC);

        if (isPCSelectedInURL && eventPlatformPC) {
            setPlatform(eventPlatformPC.platform);
        }
    }

    private trackPlatformSelect = (platformId: PLATFORM_TYPES, isConsole: boolean = false) => {
        AnalyticsService.sendFlowEvent({
            ...LANDING_FLOW_STEP,
            ...PLATFORM_BUTTON_FLOW_ACTION(platformId, isConsole),
            category: CATEGORY.INTERACTION
        });
    };

    public handlePCPlatform = (platformId: PLATFORM_TYPES) => {
        AnalyticsService.setPlatformSelection('');
        this.trackPlatformSelect(platformId);
        this.props.setPlatform(platformId);
    };

    public handleConsolePlatformCreator = (eventPlatform: IEventPlatform) => (platformId: PLATFORM_TYPES) => {
        this.trackPlatformSelect(platformId, true);
        eventPlatform.url && window.open(eventPlatform.url, '_blank');
    };

    public createPlatformButtonHandler(eventPlatform: IEventPlatform) {
        if (eventPlatform.platform === PLATFORM_TYPES.PC) {
            return this.handlePCPlatform;
        } else {
            // require to call handler creator,
            // in order to catch and keep scope data
            return this.handleConsolePlatformCreator(eventPlatform);
        }
    }

    public renderPlatformButtons(eventPlatforms: IEventPlatform[]) {
        return (
            eventPlatforms &&
            eventPlatforms.map((eventPlatform: IEventPlatform) => (
                <PlatformButton
                    disabled={this.props.disabled}
                    key={eventPlatform.platform}
                    platformId={eventPlatform.platform}
                    onClick={this.createPlatformButtonHandler(eventPlatform)}
                    hideLink
                />
            ))
        );
    }

    public createTitle = (): JSX.Element => {
        const eventPlatformPC = this.props.eventPlatforms.find(({ platform }) => platform === PLATFORM_TYPES.PC);

        const { originalTranslateKey } = this.props;
        const { startDate, endDate } = eventPlatformPC as IEventPlatform;

        const startMoment = moment.utc(startDate).local();
        const endMoment = moment.utc(endDate).local();

        const gameNameText: string = originalTranslateKey(TEXT_KEYS.GAME_NAME);

        /**
         *  GEN-1937: i18next breaks on attempts to use reserved characters ( colons(:) ) during interpolation,
         *  Time actual values changed to placeholders for further replacing
         *  with regular string values
         *  More here: [https://www.i18next.com/overview/getting-started#important-caveat]
         */
        const timeHolders = {
            START_HOUR_PLACEHOLDER: formatHours(startMoment),
            END_HOUR_PLACEHOLDER: formatHours(endMoment)
        };

        let titleTranslation: string = originalTranslateKey(TEXT_KEYS.PLATFORM_SELECTION_TITLE, {
            startMonth: localizeMonth(startMoment),
            startDay: localizeDate(startMoment),
            startHour: 'START_HOUR_PLACEHOLDER',
            endHour: 'END_HOUR_PLACEHOLDER',
            endMonth: localizeMonth(endMoment),
            endDay: localizeDate(endMoment),
            gameName: gameNameText
        });

        titleTranslation = titleTranslation.replace(/START_HOUR_PLACEHOLDER|END_HOUR_PLACEHOLDER/g, match => timeHolders[match]);

        return <span dangerouslySetInnerHTML={{ __html: titleTranslation }}/>;
    };

    public render() {
        const titleText = this.createTitle();

        return (
            <div className="sitegen-freecontent-platform-selection">
                <div className="sitegen-freecontent-platform-selection__title">
                    <p>{titleText}</p>
                </div>
                {this.props.children}
                <div className="sitegen-freecontent-platform-selection__platforms">
                    {this.renderPlatformButtons(this.props.eventPlatforms)}
                </div>
            </div>
        );
    }
}

interface IPlatformSelectionProps extends ITranslationProps {
    readonly disabled: boolean;
    readonly eventPlatforms: IEventPlatform[];
    setPlatform(platformId: PLATFORM_TYPES): void;
    readonly children: JSX.Element | React.Component | false;
}

export default translate()(PlatformSelection);
